import {
  HotelLiteModel,
  HotelModel,
  getHotelKey,
  getHotelsKey,
  useGetHotel,
  useGetHotels,
} from 'api-hooks/hotel';
import { OnChangeParams, Option } from 'baseui/select';
import { NavigationRoutes } from 'common/constants';
import { QueryTransformer } from 'common/helpers/common';
import { transformLabel } from 'common/utils/string';
import { Input } from 'components/elements';
import useGetCrudPermission from 'hooks/use-get-crud-permission';
import useNavigation from 'hooks/use-navigation';
import useSelectInputHelper from 'hooks/use-select-input-helper';
import useTranslation from 'next-translate/useTranslation';
import * as React from 'react';
import { useFormContext, useWatch } from 'react-hook-form';

function transformer(item: HotelLiteModel | HotelModel): Option {
  return {
    id: item.id,
    label: transformLabel([item.name, item.area, item.cityCountry]),
    extra: item,
  };
}

interface Props {
  name: string;
  required?: boolean;
  disabled?: boolean;
  label?: string;
  placeholder?: string;
  onClose?: () => void;
  onAfterChange?: (value?: OnChangeParams) => void;
  onAfterDetailChange?: (value: HotelModel) => void;
  noMargin?: boolean;
}

export default function HotelSelectInput(props: Props) {
  const { t } = useTranslation();
  const { label = t('common:hotel'), placeholder = t('common:select_hotel') } =
    props;
  const { control, setValue } = useFormContext<any>();
  const value = useWatch({ control, name: props.name });

  const { navigate } = useNavigation();

  const permission = useGetCrudPermission('hotels');
  const { overrides, ...selectProps } = useSelectInputHelper({
    value,
    onSelectItem: (value) => {
      setValue(props.name, value[0]?.id || '', {
        shouldTouch: true,
        shouldValidate: true,
      });
    },
    useListQueryHook: useGetHotels,
    useDetailLazyQueryHook: useGetHotel,
    listKeyFunction: getHotelsKey,
    detailKeyFunction: getHotelKey,
    getMemoizedListVariables: React.useCallback(
      (page, q) => ({
        params: {
          q,
          page,
        },
      }),
      [],
    ),
    renderCreate: permission.create,
    onClickCreate: React.useCallback(() => {
      navigate(NavigationRoutes.HotelsCreate);
      props.onClose?.();
    }, [navigate, props]),
    getMemoizedDetailVariables: React.useCallback(
      (value: string) => ({
        id: value,
      }),
      [],
    ),
    listTransformer: React.useCallback((result) => {
      return result.data.map((v) => transformer(v));
    }, []),
    detailTransformer: React.useCallback(
      (result) => {
        const newResult = QueryTransformer(result, HotelModel);
        props.onAfterDetailChange?.(newResult.data);
        return transformer(newResult.data);
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [],
    ),
    paginationTransformer: React.useCallback((result) => {
      return result.meta;
    }, []),
  });

  return (
    <Input
      type="select"
      name={props.name}
      required={props.required}
      disabled={props.disabled}
      label={label}
      placeholder={placeholder}
      onAfterChange={props.onAfterChange}
      noMargin={props.noMargin}
      overrides={overrides}
      {...selectProps}
    />
  );
}
